import React, { useState } from 'react';
import {
    ChevronRight as ChevronRightIcon,
    Code as CodeIcon,
    PhoneAndroid as SmartphoneIcon,
    Public as GlobeIcon,
    Groups as GroupsIcon,
    Bolt as BoltIcon,
    CheckCircle as CheckCircleIcon,
    ArrowRight as ArrowRightIcon,
    Star as StarIcon,
    Business as BusinessIcon,
    Favorite as FavoriteIcon,
    ShoppingBag as ShoppingBagIcon,
    LocalShipping as LocalShippingIcon,
    Restaurant as RestaurantIcon,
    CreditCard as CreditCardIcon,
    Wifi as WifiIcon,
    School as SchoolIcon,
    Home as HomeIcon,
    ShoppingCart as ShoppingCartIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Typography,
    useTheme,
    alpha
} from '@mui/material';
import { red } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';

const SoftwareDevPage = () => {
    const theme = useTheme();
    const [hoveredCard, setHoveredCard] = useState(null);
    const navigate = useNavigate();

    const services = [
        {
            icon: <CodeIcon fontSize="large" />,
            title: "Custom Software Development",
            description: "We develop bespoke solutions for specific business needs and requirements. Custom development means full-cycle software development adapted to your case regarding budget, timelines, tech stack, advanced technologies, and other aspects.",
            image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=500&h=300&fit=crop"
        },
        {
            icon: <SmartphoneIcon fontSize="large" />,
            title: "Mobile App Development",
            description: "Our team of experienced mobile developers and talented designers makes iOS, Android, or cross-platform applications with a strong focus on designing intuitive user interfaces and providing a smooth user experience.",
            image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=500&h=300&fit=crop"
        },
        {
            icon: <GlobeIcon fontSize="large" />,
            title: "Web Application Development",
            description: "We develop scalable, high-performance, and secure web applications that seamlessly integrate with your processes and grow with your business.",
            image: "https://images.unsplash.com/photo-1669023414162-8b0573b9c6b2?q=80&w=3432&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D?w=500&h=300&fit=crop"
        }
    ];

    const industries = [
        { name: "Healthcare", icon: <FavoriteIcon />, color: theme.palette.error.main },
        { name: "Media & Entertainment", icon: <StarIcon />, color: theme.palette.warning.main },
        { name: "Enterprise", icon: <BusinessIcon />, color: theme.palette.info.main },
        { name: "Logistics", icon: <LocalShippingIcon />, color: theme.palette.success.main },
        { name: "Food Tech", icon: <RestaurantIcon />, color: "#FF9800" },
        { name: "Banking & Finance", icon: <CreditCardIcon />, color: "#4CAF50" },
        { name: "IoT Management", icon: <WifiIcon />, color: "#9C27B0" },
        { name: "Education", icon: <SchoolIcon />, color: "#2196F3" },
        { name: "Real Estate", icon: <HomeIcon />, color: "#00BCD4" },
        { name: "eCommerce", icon: <ShoppingCartIcon />, color: "#E91E63" },
        { name: "Marketing", icon: <TrendingUpIcon />, color: "#FF5722" }
    ];

    return (
        <Box sx={{ bgcolor: 'background.default' }}>
            {/* Hero Section */}
            <Box sx={{
                position: 'relative',
                bgcolor: 'rgba(63, 81, 181, 0.9)',
                color: 'primary.contrastText',
                py: 12,
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
                    zIndex: 1
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Stack alignItems="center" textAlign="center" spacing={4}>
                        <Chip
                        onClick={()=>console.log("")}
                            icon={<BoltIcon />}
                            label="End-to-end Custom Software Solutions"
                            sx={{
                                bgcolor: 'rgba(208, 227, 239, 0.55)',
                                color: 'primary.contrastText',
                                px: 2,
                                py: 1,
                                fontSize: '0.875rem'
                            }}
                        />

                        <Typography variant="h2" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                            Software Product <Box component="span" sx={{ color: '#61dafb' }}>Development</Box>
                        </Typography>

                        <Typography variant="h5" sx={{ maxWidth: 800, opacity: 0.9 }}>
                            Transform your vision into powerful digital solutions with our comprehensive software development services.
                            From concept to deployment, we deliver excellence at every step.
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="large"
                                endIcon={<ArrowRightIcon />}
                                onClick={() => navigate('/get-in-touch')}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    '&:hover': {
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                Get Started
                            </Button>

                            <Button
                                variant="outlined"
                                color="inherit"
                                size="large"
                                onClick={() => navigate('/case-studies')}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    borderColor: alpha(theme.palette.common.white, 0.3),
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        borderColor: theme.palette.common.white,
                                        bgcolor: alpha(theme.palette.common.white, 0.1)
                                    }
                                }}
                            >
                                View Our Work
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* Services Section */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Stack spacing={6} alignItems="center" textAlign="center">
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
                        Software Product Development Services
                    </Typography>

                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700 }}>
                        Comprehensive solutions tailored to your business needs and requirements
                    </Typography>

                    <Grid container spacing={4}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card
                                    elevation={hoveredCard === index ? 6 : 2}
                                    onMouseEnter={() => setHoveredCard(index)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    sx={{
                                        height: '100%',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)'
                                        }
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={service.image}
                                        alt={service.title}
                                    />

                                    <CardContent sx={{ p: 4 }}>
                                        <Stack direction="row" spacing={3} alignItems="center" mb={3}>
                                            <Box
                                                sx={{
                                                    width: 64,
                                                    height: 64,
                                                    bgcolor: 'primary.light',
                                                    color: 'primary.contrastText',
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    transition: 'all 0.3s ease',
                                                    ...(hoveredCard === index && {
                                                        transform: 'scale(1.1)',
                                                        bgcolor: 'secondary.main'
                                                    })
                                                }}
                                            >
                                                {service.icon}
                                            </Box>
                                            <Typography variant="h5" component="h3">
                                                {service.title}
                                            </Typography>
                                        </Stack>

                                        <Typography variant="body1" color="text.secondary" paragraph>
                                            {service.description}
                                        </Typography>

                                        <Button
                                            endIcon={<ChevronRightIcon sx={{
                                                transition: 'transform 0.3s ease',
                                                transform: hoveredCard === index ? 'translateX(4px)' : 'none'
                                            }} />}
                                            onClick={()=> navigate('/services')}
                                            sx={{
                                                px: 0,
                                                color: 'primary.main',
                                                '&:hover': {
                                                    color: 'secondary.main',
                                                    bgcolor: 'transparent'
                                                }
                                            }}
                                        >
                                            Learn More
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Container>

            {/* Development Models Section */}
            <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
                <Container maxWidth="lg">
                    <Stack spacing={6} alignItems="center" textAlign="center">
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
                            Which Product Development Service Model You Need?
                        </Typography>

                        <Typography variant="h6" color="text.secondary">
                            Choose the approach that best fits your project requirements
                        </Typography>

                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        height: '100%',
                                        p: 4,
                                        textAlign: 'left',
                                        // borderLeft: `4px solid ${theme.palette.primary.main}`,
                                        '&:hover': {
                                            boxShadow: theme.shadows[6]
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center" mb={4}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <CodeIcon fontSize="medium" />
                                        </Box>
                                        <Typography variant="h5" component="h3">
                                            Product Development Outsourcing
                                        </Typography>
                                    </Stack>

                                    <Typography variant="body1" color="text.secondary" paragraph>
                                        Our product development outsourcing services transform your vision into fully-fledged software.
                                        Whether launching a new product or upgrading an existing one, our expert team can lead you through
                                        the project development life cycle.
                                    </Typography>

                                    <List dense sx={{ mb: 4 }}>
                                        {[
                                            "Full-cycle product development",
                                            "Usage of the latest technologies like AI, ML, Computer Vision, etc.",
                                            "Well-established development processes",
                                            "Cost-effective and scalable solutions"
                                        ].map((item, index) => (
                                            <ListItem key={index} disableGutters>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    <CheckCircleIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText primary={item} />
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={()=> navigate('/get-in-touch')}
                                        size="large"
                                    >
                                        Get in Touch
                                    </Button>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        height: '100%',
                                        p: 4,
                                        textAlign: 'left',
                                        // borderLeft: `4px solid ${theme.palette.secondary.main}`,
                                        '&:hover': {
                                            boxShadow: theme.shadows[6]
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center" mb={4}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                bgcolor: 'secondary.light',
                                                color: 'secondary.contrastText',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <GroupsIcon fontSize="medium" />
                                        </Box>
                                        <Typography variant="h5" component="h3">
                                            Staff Outsourcing
                                        </Typography>
                                    </Stack>

                                    <Typography variant="body1" color="text.secondary" paragraph>
                                        We expand in-house teams with flexible staff Outsourcing services, providing the expertise of
                                        specialists you need. Whether it's a lack of specialized skills or the need for additional workforces,
                                        we can seamlessly extend your development team.
                                    </Typography>

                                    <List dense sx={{ mb: 4 }}>
                                        {[
                                            "Short and long-term cooperation",
                                            "Web, mobile developers, designers, scrum masters, business analysts, QA",
                                            "Expertise of top senior-level specialists",
                                            "On-demand scaling"
                                        ].map((item, index) => (
                                            <ListItem key={index} disableGutters>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    <CheckCircleIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText primary={item} />
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        fullWidth
                                        onClick={()=> navigate('/out-sourcing')}
                                        size="large"
                                    >
                                        Learn More
                                    </Button>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Stack>
                </Container>
            </Box>

            {/* Industries Section */}
  <Container maxWidth="lg" sx={{ py: 10 }}>
  <Stack spacing={6} alignItems="center" textAlign="center">
    <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
      Industry-Specific Software Product Development
    </Typography>

    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700 }}>
      We develop custom software products tailored to specific industries. Our expertise includes over 250+ completed projects across 12 industries.
    </Typography>

    <Grid container spacing={4} justifyContent="center">
      {industries.map((industry, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Box
            sx={{
              p: 4,
              height: '100%',
              borderRadius: '20px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
            //   background: `linear-gradient(135deg, ${alpha(industry.color, 0.05)}, ${alpha(industry.color, 0.02)})`,
            //   border: `1.5px solid ${alpha(industry.color, 0.3)}`,
              boxShadow: `0 8px 30px ${alpha(industry.color, 0.2)}`,
              transition: 'all 0.4s ease-in-out',
              '&:hover': {
                transform: 'translateY(-6px) scale(1.03)',
                boxShadow: `0 12px 40px ${alpha(industry.color, 0.4)}`
              }
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                mb: 2,
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `radial-gradient(circle at 30% 30%, ${alpha(industry.color, 0.3)}, ${alpha(industry.color, 0.1)})`,
                color: industry.color,
              }}
            >
              {React.cloneElement(industry.icon, { fontSize: 'large' })}
            </Box>

            <Typography variant="subtitle1" fontWeight={600} sx={{ color: industry.color }}>
              {industry.name}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Stack>
</Container>



            {/* CTA Section */}
            <Box sx={{
                bgcolor: 'rgba(17, 135, 208, 0.82)',
                color: 'primary.contrastText',
                py: 10,
                backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
            }}>
                <Container maxWidth="lg">
                    <Stack spacing={4} alignItems="center" textAlign="center">
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
                            Ready to Transform Your Ideas?
                        </Typography>

                        <Typography variant="h6" sx={{ maxWidth: 700, opacity: 0.9 }}>
                            Let's discuss your project and create something amazing together. Our team is ready to bring your vision to life.

                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => navigate('/get-in-touch')}
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem'
                                }}
                            >
                                Start Your Project
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => navigate('/get-in-touch')}
                                color="inherit"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    borderColor: alpha(theme.palette.common.white, 0.3),
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        borderColor: theme.palette.common.white,
                                        bgcolor: 'rgba(17, 135, 208, 0.82)',
                                    }
                                }}
                            >
                                Schedule Consultation
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default SoftwareDevPage;